//React
import { useState, useEffect, useContext } from 'react';
import { Route, useNavigate, Routes } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
// Microsoft
import { app, monetization } from '@microsoft/teams-js';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
// Utils
import { callFunction, loadAppsData } from '../../lib/Helpers';
import { useBoolean } from '@fluentui/react-hooks';
// Types
import { EntObject } from '../../pages/MyHub';
// Apps
import TranslatorApp from '../../appsPages/TranslatorApp';
import { NewTts } from '../../appsPages/NewTts';
// Components
import { LeftMenu } from '../../widgets/LeftMenu';
import { BasePage } from '../../widgets/BasePage';
import { AppObject } from '../../widgets/AppCard';
import NoApps from '../../widgets/NoApps';
import SSO from '../../widgets/SSO';
import { Alert } from '@fluentui/react-components/unstable';
import { TrialAlert } from '../../widgets/TrialAlert';
// Contexts
import { AppsMenuContext } from '../../contexts/AppsMenuContext';
import { TtsContextProvider } from '../../contexts/TtsContext';
// Styling
import { MyAppsContainer, MyAppsContent } from './styles';
import { XCircle } from '@phosphor-icons/react';
import { TeamsFxContext } from '../../Context';
import { useTranslation } from 'react-i18next';
import { NewTtsPreview } from '../../appsPages/NewTtsPreview';
import { QuotaContextProvider } from '../../contexts/QuotaContext';
import { LicencePopup } from '../../widgets/LicencePopup';

interface PageProps {
  context: app.Context | undefined;
}

export const MyApps = ({ context }: PageProps) => {
  const userId = context?.user?.id ?? '';
  const tenantId = context?.user?.tenant?.id ?? '';
  const teamsUserCredential = useContext(TeamsFxContext).teamsUserCredential;

  const appInsights = useAppInsightsContext();

  const { t } = useTranslation(['myapps', 'alerts']);

  const [isSSO, setIsSSO] = useState<boolean>(false);
  const [exception, setException] = useState<string>('');

  const [userEntitlements, setUserEntitlements] = useState<EntObject>();
  const [allApps, setAllApps] = useState<AppObject[] | undefined>();
  const [userApps, setUserApps] = useState<AppObject[] | undefined>();

  const [isTrial, { setTrue: inTrial, setFalse: notInTrial }] =
    useBoolean(false);

  const { handleApps } = useContext(AppsMenuContext);

  const [isLicencePopupVisible, setIsLicencePopupVisible] =
    useState<boolean>(false);
  const [licencePopupType, setLicencePopupType] = useState<
    'TRIAL' | 'HAS_TRIED'
  >();
  const [hasShownLicencePopup, setHasShownLicencePopup] =
    useState<boolean>(false);

  useEffect(() => {
    app.notifySuccess();
  }, []);

  // Load Data
  useEffect(() => {
    if (userId) {
      loadAppsData(
        userId,
        setAllApps,
        setUserEntitlements,
        tenantId as string,
        teamsUserCredential
      ).catch((error) => {
        if (process.env.REACT_APP_APP_INSIGHTS !== '')
          appInsights.trackException({
            exception: error,
            properties: { component: 'MyApps', location: 'onLoadAppsData' },
          });
      });
    }
  }, [appInsights, teamsUserCredential, tenantId, userId]);

  // Get User Apps
  useEffect(() => {
    if (userEntitlements) {
      if (!hasShownLicencePopup && userEntitlements.subscriptions.length <= 0) {
        userEntitlements.hasTried
          ? setLicencePopupType('HAS_TRIED')
          : setLicencePopupType('TRIAL');
        setIsLicencePopupVisible(true);
        setHasShownLicencePopup(true);
      }

      userEntitlements.subscriptions.forEach((sub) => {
        if (sub.isFreeTrial && userEntitlements.subscriptions.every(() => sub))
          inTrial();
      });

      setUserApps(
        allApps?.filter((app) => userEntitlements.apps.includes(app.id))
      );
    }
  }, [allApps, inTrial, userEntitlements]);

  // Get Menu Apps
  useEffect(() => {
    userApps && handleApps(userApps);
  }, [handleApps, userApps]);

  const [activeLink, setActiveLink] = useState('home');
  const navigate = useNavigate();

  const navigateToMenu = (key: string) => {
    setActiveLink(key);
    navigate(key);
    appInsights.trackEvent({
      name: 'Left Menu Clicked',
      properties: { menuItem: key },
    });
  };

  const openPurchaseExperience = () => {
    if (process.env.REACT_APP_APP_INSIGHTS !== '') {
      appInsights.trackEvent({ name: 'Upgrade from MyApps clicked' });
      if (monetization.isSupported()) {
        monetization
          .openPurchaseExperience()
          .catch(() =>
            window.open(
              encodeURI(
                'https://appsource.microsoft.com/en-us/product/web-apps/bitfractal1649005178126.dd1d8735-7a73-4914-929a-4f9fe0be6b75?tab=PlansAndPrice'
              )
            )
          );
      } else {
        window.open(
          encodeURI(
            'https://appsource.microsoft.com/en-us/product/web-apps/bitfractal1649005178126.dd1d8735-7a73-4914-929a-4f9fe0be6b75?tab=PlansAndPrice'
          )
        );
      }
    }
  };

  const initiateTrialExperience = () => {
    callFunction(
      true,
      'putTrialSubForUser?{0}&tenantId=' + tenantId,
      undefined,
      false,
      teamsUserCredential
    )
      .then(() => {
        if (process.env.REACT_APP_APP_INSIGHTS !== '')
          appInsights.trackEvent({
            name: 'Trial initiated from MyApps clicked',
          });
        window.location.reload();
      })
      .catch(() => {
        alert(
          'Unable to initiate trial, please try again later or contact support if the problem persists.'
        );
      });
  };

  return (
    <>
      {isTrial && <TrialAlert />}
      {!context && (
        <Alert intent="warning" appearance="inverted">
          {t('NOT_IN_TEAMS', { ns: 'alerts' })}
        </Alert>
      )}
      {exception !== '' && (
        <Alert
          intent="error"
          action={{
            icon: <XCircle color="#fd6262" />,
            onClick: () => setException(''),
          }}
        >
          {t('APP_ERROR', { ns: 'alerts' })}
        </Alert>
      )}

      {!isMobile ? (
        <MyAppsContainer>
          <LeftMenu
            type="apps"
            onLinkClick={navigateToMenu}
            isTrial={isTrial}
          />
          <MyAppsContent>
            <SSO isSSO={isSSO} setIsSSO={setIsSSO} />
            {activeLink === 'home' &&
              userEntitlements?.apps &&
              userEntitlements.apps.length > 0 && (
                <BasePage text={t('BASE_PAGE_TEXT')} />
              )}

            {isSSO && userEntitlements?.apps?.length === 0 && (
              <NoApps
                onUpgrade={openPurchaseExperience}
                hasTried={userEntitlements.hasTried}
                tid={tenantId}
              />
            )}

            <QuotaContextProvider>
              <TtsContextProvider>
                <Routes>
                  <Route path="/translator" element={<TranslatorApp />} />
                  <Route
                    path="/tts"
                    element={
                      <NewTts
                        isTrial={isTrial}
                        appId="798cf522-7099-4391-b739-60e013002413"
                        firstRunEnabled={
                          !userEntitlements?.tutorials.includes(
                            '798cf522-7099-4391-b739-60e013002413'
                          )
                        }
                        tid={tenantId}
                        currentUser={userId}
                      />
                    }
                  />
                  <Route
                    path="/ttspreview"
                    element={
                      <NewTtsPreview
                        isTrial={isTrial}
                        appId="798cf522-7099-4391-b739-60e013002413"
                        firstRunEnabled={
                          !userEntitlements?.tutorials.includes(
                            '798cf522-7099-4391-b739-60e013002413'
                          )
                        }
                        tid={tenantId}
                        currentUser={userId}
                      />
                    }
                  />
                </Routes>
              </TtsContextProvider>
            </QuotaContextProvider>

            {licencePopupType && (
              <LicencePopup
                isVisible={isLicencePopupVisible}
                setIsVisible={setIsLicencePopupVisible}
                tid={tenantId}
                type={licencePopupType}
              />
            )}
          </MyAppsContent>
        </MyAppsContainer>
      ) : (
        <MyAppsContainer>
          <QuotaContextProvider>
            <TtsContextProvider>
              <MyAppsContent>
                <SSO isSSO={isSSO} setIsSSO={setIsSSO} />
                {isSSO &&
                  userEntitlements?.apps?.length !== 0 &&
                  activeLink === 'home' && (
                    <NewTts
                      isTrial={isTrial}
                      appId="798cf522-7099-4391-b739-60e013002413"
                      firstRunEnabled={userEntitlements?.tutorials.includes(
                        '798cf522-7099-4391-b739-60e013002413'
                      )}
                      tid={tenantId}
                      currentUser={userId}
                      isMobile
                    />
                  )}

                {isSSO && userEntitlements?.apps?.length === 0 && (
                  <NoApps
                    onUpgrade={openPurchaseExperience}
                    hasTried={userEntitlements.hasTried}
                    tid={tenantId}
                  />
                )}
              </MyAppsContent>
            </TtsContextProvider>
          </QuotaContextProvider>
        </MyAppsContainer>
      )}
    </>
  );
};
